// Medical Portal — Query / RCA Details panel
//
// Presentational body shared by:
//   • QueryDetailModal — opened from the Report Queries tab
//   • ReportDetailModal — when a query already exists for the report's
//     visitor_id, this panel renders inline beneath the report (instead
//     of the "Raise Query" CTA), so the doctor sees status tracking +
//     details + attachments without a second click.
//
// The list API returns everything we need EXCEPT `state.incident`
// (source / detector / branch / timings). The optional `isFetching`
// prop lets the host show a "loading details…" hint while the detail
// fetch fills in those fields.
import { useMemo } from 'react';
import Box from '@mui/material/Box';
import Grid from '@mui/material/Grid';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import DescriptionOutlinedIcon from '@mui/icons-material/DescriptionOutlined';
import ImageOutlinedIcon from '@mui/icons-material/ImageOutlined';
import PictureAsPdfOutlinedIcon from '@mui/icons-material/PictureAsPdfOutlined';
import { MP, MONO, DISPLAY, StatusBadge, Pill } from './shared';

// Lifecycle the backend reports via `view_status` (1..4). Kept to four
// clear stages — "Response" was redundant with "Investigating", and
// resolved/closed always moved together so they're merged.
const STEPS = [
  { key: 'received',         label: 'Received' },
  { key: 'acknowledged',     label: 'Acknowledged' },
  { key: 'investigating',    label: 'Investigating' },
  { key: 'resolved_closed',  label: 'Resolved & Closed' },
];

const Stepper = ({ stage }) => (
  <Stack
    direction="row"
    alignItems="center"
    justifyContent="center"
    sx={{ overflowX: 'auto', pb: 1 }}
  >
    {STEPS.map((s, i) => {
      const done = i <= stage;
      const current = i === stage;
      return (
        <Stack key={s.key} direction="row" alignItems="center" sx={{ flexShrink: 0 }}>
          <Stack alignItems="center" sx={{ minWidth: 82 }} spacing={0.75}>
            <Box sx={{
              width: 32, height: 32, borderRadius: '50%',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              bgcolor: done ? MP.teal : MP.bg,
              color: done ? '#fff' : MP.textMuted,
              border: `2px solid ${current ? MP.tealDark : done ? MP.teal : MP.border}`,
              fontSize: 13, fontWeight: 700,
              boxShadow: current ? `0 0 0 4px ${MP.tealLight}` : 'none',
            }}>
              {done ? '✓' : i + 1}
            </Box>
            <Typography sx={{
              fontSize: 10.5, fontFamily: MONO, fontWeight: 700,
              letterSpacing: '0.06em', textTransform: 'uppercase',
              color: done ? MP.tealDark : MP.textMuted,
              textAlign: 'center',
            }}>
              {s.label}
            </Typography>
          </Stack>
          {i < STEPS.length - 1 && (
            <Box sx={{
              width: { xs: 22, sm: 40 }, height: 2,
              bgcolor: i < stage ? MP.teal : MP.border,
              mx: 0.25, mt: -2.25,
            }} />
          )}
        </Stack>
      );
    })}
  </Stack>
);

// "2026-04-24T16:04:45+00:00" → "24 Apr 2026, 04:04 PM"
export const prettyDateTime = (iso) => {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  const datePart = d.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
  const timePart = d.toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit', hour12: true });
  return `${datePart}, ${timePart}`;
};

// Bytes → human size
const humanSize = (bytes) => {
  if (bytes == null) return '';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(2)} MB`;
};

const fileIcon = (mime = '', name = '') => {
  const m = mime.toLowerCase();
  const n = name.toLowerCase();
  if (m.startsWith('image/') || /\.(png|jpe?g|webp|gif|heic|heif)$/.test(n)) {
    return <ImageOutlinedIcon sx={{ fontSize: 22, color: MP.teal }} />;
  }
  if (m.includes('pdf') || n.endsWith('.pdf')) {
    return <PictureAsPdfOutlinedIcon sx={{ fontSize: 22, color: '#D93025' }} />;
  }
  return <DescriptionOutlinedIcon sx={{ fontSize: 22, color: MP.textMuted }} />;
};

// Header strip — code + status pill + patient/general pill + raised-on
// line. Optional. Hosts that already render their own header (e.g. the
// QueryDetailModal title bar) can omit it via `showHeader={false}`.
const PanelHeader = ({ record, isFetching, hasIncident }) => {
  const isPatient = !!record?.visitor_id;
  const statusText = useMemo(() => {
    const s = record?.view_status_label || 'Received';
    return s.charAt(0).toUpperCase() + s.slice(1).toLowerCase();
  }, [record]);

  return (
    <Box sx={{ mb: 2 }}>
      <Stack direction="row" alignItems="center" spacing={1.25} flexWrap="wrap" useFlexGap>
        <Typography sx={{
          fontFamily: DISPLAY, fontSize: 13.5, fontWeight: 700, letterSpacing: '.02em',
          color: MP.dark, lineHeight: 1.2,
        }}>
          {record?.code ? `QUERY · ${record.code}` : 'QUERY DETAILS'}
        </Typography>
        <StatusBadge>{statusText}</StatusBadge>
        <Pill tone={isPatient ? 'teal' : 'yellow'}>
          {isPatient ? 'Patient-specific' : 'General service'}
        </Pill>
      </Stack>
      <Typography sx={{ fontSize: 12, color: MP.textMuted, mt: 0.5, fontFamily: MONO }}>
        {record?.nc_id && (
          <>
            <Box component="span" sx={{ color: MP.tealDark, fontWeight: 700 }}>{record.nc_id}</Box>
            {record?.created_at && <> {' · Raised on '}{prettyDateTime(record.created_at)}</>}
            {isFetching && !hasIncident && <> {' · '}<Box component="span" sx={{ color: MP.teal }}>loading details…</Box></>}
          </>
        )}
      </Typography>
    </Box>
  );
};

const RCARow = ({ label, body }) => (
  <Box>
    <Typography sx={{
      fontSize: 11, fontWeight: 700, color: MP.purple,
      letterSpacing: '0.06em', textTransform: 'uppercase', mb: 0.25,
    }}>
      {label}
    </Typography>
    <Typography sx={{ fontSize: 13, color: MP.text, lineHeight: 1.6, whiteSpace: 'pre-wrap' }}>
      {body}
    </Typography>
  </Box>
);

const QueryDetailsPanel = ({ record, isFetching = false, showHeader = true }) => {
  if (!record) return null;
  const incident = record?.state?.incident;
  const evidence = incident?.evidence || record?.attachments || [];
  // view_status is 1-based per the API sample; map to 0-indexed stage.
  const stage = Math.max(0, Math.min(STEPS.length - 1, (record?.view_status ?? 1) - 1));
  const isPatient = !!record?.visitor_id;

  return (
    <Box>
      {showHeader && (
        <PanelHeader
          record={record}
          isFetching={isFetching}
          hasIncident={!!incident}
        />
      )}

      {/* Activity stepper */}
      <Box sx={{
        border: `1px solid ${MP.border}`, borderRadius: '6px',
        bgcolor: MP.bg, px: 2.5, py: 2.25, mb: 2.5,
      }}>
        <Typography sx={{
          fontSize: 10, fontWeight: 700, fontFamily: MONO,
          letterSpacing: '0.1em', color: MP.textMuted, mb: 1.25,
        }}>
          ACTIVITY TIMELINE
        </Typography>
        <Stepper stage={stage} />
      </Box>

      {/* Details grid — pulled from state.incident and top-level record */}
      <Grid container spacing={2} sx={{ mb: 2.5 }}>
        {[
          ['RCA CODE',     record.code,                                   true],
          ['NC ID',        record.nc_id,                                  true],
          isPatient && ['VISITOR ID',   record.visitor_id,               true],
          ['SOURCE',       incident?.source],
          ['DETECTOR',     incident?.detector],
          ['BRANCH',       incident?.branch],
          ['DETECTED AT',  prettyDateTime(incident?.detectedAt),          true],
          ['OCCURRED AT',  prettyDateTime(incident?.occurredAt),          true],
          ['METHOD',       record.method?.toUpperCase?.() || record.method],
        ].filter(Boolean).map(([k, v, mono]) => (
          <Grid key={k} item xs={12} sm={6} md={4}>
            <Box sx={{
              bgcolor: MP.white, border: `1px solid ${MP.border}`,
              borderRadius: '6px', px: 2, py: 1.5, height: '100%',
              boxShadow: MP.shadow,
            }}>
              <Typography sx={{
                fontSize: 10, color: MP.textMuted, fontFamily: MONO,
                letterSpacing: '0.1em', textTransform: 'uppercase',
              }}>
                {k}
              </Typography>
              <Typography sx={{
                fontSize: 13, fontWeight: 600, color: MP.dark, mt: 0.375,
                fontFamily: mono ? MONO : undefined,
                wordBreak: 'break-word',
              }}>
                {v || '—'}
              </Typography>
            </Box>
          </Grid>
        ))}
      </Grid>

      {/* Description */}
      <Box sx={{
        border: `1px solid ${MP.border}`, borderRadius: '6px',
        px: 2.25, py: 2, mb: 2.5,
      }}>
        <Typography sx={{
          fontSize: 10, fontWeight: 700, fontFamily: MONO,
          letterSpacing: '0.1em', color: MP.textMuted, mb: 0.75,
        }}>
          DESCRIPTION
        </Typography>
        <Typography sx={{ fontSize: 13.5, color: MP.text, lineHeight: 1.7, whiteSpace: 'pre-wrap' }}>
          {incident?.description || record.description || '(No description provided.)'}
        </Typography>
      </Box>

      {/* Attachments */}
      {evidence.length > 0 && (
        <Box sx={{ mb: 2.5 }}>
          <Typography sx={{
            fontFamily: DISPLAY, fontSize: 13.5, fontWeight: 700, letterSpacing: '.02em',
            color: MP.dark, mb: 1.25,
          }}>
            Attachments ({evidence.length})
          </Typography>
          <Stack spacing={1}>
            {evidence.map((f, i) => (
              <Box
                key={`${f.url}-${i}`}
                component="a"
                href={f.url}
                target="_blank"
                rel="noopener noreferrer"
                sx={{
                  display: 'flex', alignItems: 'center', gap: 1.5,
                  px: 1.75, py: 1.25,
                  border: `1px solid ${MP.border}`,
                  bgcolor: MP.white,
                  borderRadius: '6px',
                  boxShadow: MP.shadow,
                  textDecoration: 'none',
                  transition: 'all .15s',
                  '&:hover': { borderColor: MP.teal, bgcolor: MP.tealLight },
                }}
              >
                <Box
                  sx={{
                    width: 40, height: 40, borderRadius: '6px',
                    bgcolor: MP.bg, border: `1px solid ${MP.border}`,
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    flexShrink: 0,
                  }}
                >
                  {fileIcon(f.mime, f.name)}
                </Box>
                <Box sx={{ minWidth: 0, flex: 1 }}>
                  <Typography sx={{ fontSize: 13, fontWeight: 700, color: MP.dark }} noWrap>
                    {f.name || `Attachment ${i + 1}`}
                  </Typography>
                  <Typography sx={{ fontFamily: MONO, fontSize: 11, color: MP.textMuted }}>
                    {humanSize(f.size)}{f.uploadedAt ? ` · uploaded ${prettyDateTime(f.uploadedAt)}` : ''}
                  </Typography>
                </Box>
                <Typography sx={{ fontSize: 11, fontWeight: 700, color: MP.tealDark, whiteSpace: 'nowrap' }}>
                  View →
                </Typography>
              </Box>
            ))}
          </Stack>
        </Box>
      )}

      {/* Complaint — the same query as registered in Trust OPM: the
          department the CSD assigned, the ticket, and the replies the
          customer is meant to see. Read-only here. */}
      {record.complaint?.no && (
        <Box sx={{
          border: `1px solid ${MP.border}`, borderRadius: '6px',
          px: 2.25, py: 2, mb: 2.5, bgcolor: MP.white, boxShadow: MP.shadow,
        }}>
          <Stack direction="row" alignItems="center" spacing={1.25} flexWrap="wrap" useFlexGap sx={{ mb: 1.25 }}>
            <Typography sx={{
              fontSize: 10, fontWeight: 700, fontFamily: MONO,
              letterSpacing: '0.1em', color: MP.textMuted,
            }}>
              COMPLAINT
            </Typography>
            <Typography sx={{ fontFamily: MONO, fontSize: 12, fontWeight: 700, color: MP.tealDark }}>
              {record.complaint.no}
            </Typography>
            <StatusBadge>{record.complaint.status_label}</StatusBadge>
            {record.complaint.department && (
              <Pill tone="yellow">{record.complaint.department}</Pill>
            )}
            {record.complaint.ticket_code && (
              <Typography sx={{ fontFamily: MONO, fontSize: 11, color: MP.textMuted }}>
                Ticket {record.complaint.ticket_code}
                {record.complaint.ticket_status ? ` · ${record.complaint.ticket_status}` : ''}
              </Typography>
            )}
          </Stack>
          {(record.complaint.replies || []).length === 0 ? (
            <Typography sx={{ fontSize: 12.5, color: MP.textMuted }}>
              {record.complaint.department
                ? 'With the department. Replies will appear here.'
                : 'Received by the Customer Service Desk, awaiting assignment to a department.'}
            </Typography>
          ) : (
            <Stack spacing={1}>
              {record.complaint.replies.map((m) => {
                const mine = m.author_type === 'customer';
                return (
                  <Box
                    key={m.id || m.at}
                    sx={{
                      alignSelf: mine ? 'flex-end' : 'flex-start',
                      maxWidth: '88%',
                      px: 1.75, py: 1.25, borderRadius: '10px',
                      bgcolor: mine ? MP.bg : MP.tealLight,
                      border: `1px solid ${mine ? MP.border : MP.teal}33`,
                    }}
                  >
                    <Typography sx={{ fontFamily: MONO, fontSize: 10.5, color: MP.textMuted, mb: 0.375 }}>
                      {mine ? 'You' : (m.author_name || 'TrustLab')} · {prettyDateTime(m.at)}
                    </Typography>
                    <Typography sx={{ fontSize: 13, color: MP.text, lineHeight: 1.6, whiteSpace: 'pre-wrap' }}>
                      {m.message}
                    </Typography>
                  </Box>
                );
              })}
            </Stack>
          )}
        </Box>
      )}

      {/* RCA findings — only when something's been filled in */}
      {(record.root_cause || record.apparent_cause || record.contributing_factors) && (
        <Box sx={{
          bgcolor: MP.purpleLight,
          border: `1px solid ${MP.purple}33`,
          borderRadius: '6px', px: 2.25, py: 2, mt: 1,
        }}>
          <Typography sx={{
            fontSize: 10, fontWeight: 700, fontFamily: MONO,
            color: MP.purple, letterSpacing: '0.1em', mb: 1,
          }}>
            ROOT CAUSE ANALYSIS (READ-ONLY)
          </Typography>
          <Stack spacing={1.25}>
            {record.apparent_cause && <RCARow label="Apparent cause" body={record.apparent_cause} />}
            {record.root_cause && <RCARow label="Root cause" body={record.root_cause} />}
            {record.contributing_factors && <RCARow label="Contributing factors" body={record.contributing_factors} />}
            {record.external_root_cause && <RCARow label="External root cause" body={record.external_root_cause} />}
          </Stack>
        </Box>
      )}
    </Box>
  );
};

// Status helper — `record?.view_status_label` mapped to title-case so
// callers can decide whether to show "Escalate" / hide CTAs cleanly
// without re-implementing the lowercasing rule.
export const statusOf = (record) => {
  const s = record?.view_status_label || 'Received';
  return s.charAt(0).toUpperCase() + s.slice(1).toLowerCase();
};

export default QueryDetailsPanel;
